import { Outlet, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { House, Calendar, Receipt, SignOut, Ruler, Package, EyeSlash, Camera, Bell, AirplaneTilt, CreditCard, User, Barbell, CheckSquare, ClipboardText, Signature, UsersFour, MapTrifold } from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";
import { usePrefs } from "../../context/PrefsContext";
import { useBrand } from "../../context/BrandContext";
import Logo from "../../components/Logo";
import BotChat from "../../components/BotChat";
import WhatsAppBubble from "../../components/WhatsAppBubble";
import { GetAppButton } from "../../components/InstallGuide";
import PrefsToggle from "../../components/PrefsToggle";
import NotificationBell from "../../components/NotificationBell";
import api from "../../lib/api";

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const { privacy } = usePrefs();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const onLogout = async () => { await logout(); navigate("/", { replace: true }); };

  // Onboarding gate: when a new client lands without finishing the welcome
  // wizard AND without an active Direct Debit, send them to /client/welcome.
  // The wizard itself lives at /client/welcome and is rendered outside this layout.
  const [gateChecked, setGateChecked] = useState(false);
  const [needsWizard, setNeedsWizard] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!user || user.role !== "client") { setGateChecked(true); return; }
    if (user.onboarded_at) { setGateChecked(true); return; }
    (async () => {
      try {
        const { data } = await api.get("/clients/me/payment-method");
        if (cancelled) return;
        if (!data?.has_method) setNeedsWizard(true);
      } catch (_) {/* ignore — let them through */}
      finally { if (!cancelled) setGateChecked(true); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (gateChecked && needsWizard && location.pathname !== "/client/welcome") {
    return <Navigate to="/client/welcome" replace />;
  }

  const items = [
    { to: "/client", end: true, icon: <House size={18} weight="duotone" />, label: "Home" },
    { to: "/client/sessions", icon: <Calendar size={18} weight="duotone" />, label: "Calendar" },
    { to: "/client/metrics", icon: <Ruler size={18} weight="duotone" />, label: "Metrics" },
    { to: "/client/assessment", icon: <Barbell size={18} weight="duotone" />, label: "Assessment" },
    { to: "/client/progress", icon: <Camera size={18} weight="duotone" />, label: "Progress" },
    { to: "/client/habits", icon: <CheckSquare size={18} weight="duotone" />, label: "Habits" },
    { to: "/client/checkin", icon: <ClipboardText size={18} weight="duotone" />, label: "Check-in" },
    { to: "/client/forms", icon: <Signature size={18} weight="duotone" />, label: "Forms" },
    { to: "/client/invoices", icon: <Receipt size={18} weight="duotone" />, label: "Payments" },
    { to: "/client/payment-method", icon: <CreditCard size={18} weight="duotone" />, label: "Direct Debit" },
    { to: "/client/packages", icon: <Package size={18} weight="duotone" />, label: "Packages" },
    { to: "/client/group", icon: <UsersFour size={18} weight="duotone" />, label: "Classes" },
    { to: "/client/discover", icon: <MapTrifold size={18} weight="duotone" />, label: "Discover" },
    { to: "/client/away", icon: <AirplaneTilt size={18} weight="duotone" />, label: "Holiday" },
    { to: "/client/profile", icon: <User size={18} weight="duotone" />, label: "Profile" },
    { to: "/client/settings", icon: <Bell size={18} weight="duotone" />, label: "Settings" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="client-layout">
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[#0A0A0A]/95 backdrop-blur">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {brand.brand_logo_b64 ? (
              <img src={brand.brand_logo_b64} alt={(brand.brand_name || brand.trainer_name || "logo") + " logo"} className="h-7 w-7 object-contain" data-testid="brand-logo-header" />
            ) : (
              <Logo size={28} />
            )}
            <div>
              <div className="font-display text-xl leading-none uppercase" data-testid="brand-name-header">
                {brand.brand_name || "TRAINA"}
              </div>
              <div className="tracker text-zinc-500 mt-1 hidden md:block">
                {brand.brand_name ? `with ${brand.trainer_name || "your coach"}` : "Run your training business with confidence"}
              </div>
            </div>
            {privacy && (
              <span
                className="tracker px-2 py-1 border border-brand/50 bg-brand/10 text-brand-light hidden sm:inline-flex items-center gap-1.5 ml-2"
                data-testid="privacy-badge"
                title="Privacy mode — financial figures are blurred"
              >
                <EyeSlash size={11} weight="bold" />
                Privacy on
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <PrefsToggle />
            <NotificationBell />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="tracker">{user?.email}</div>
            </div>
            <button onClick={onLogout} className="btn-ghost !py-2 !px-3" data-testid="client-logout">
              <SignOut size={16} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto min-w-0">
        <div className="border-b border-white/10">
          <div className="flex overflow-x-auto min-w-0">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-6 py-4 tracker whitespace-nowrap ${
                    isActive ? "text-white border-b-2 border-brand" : "text-zinc-400 hover:text-white"
                  }`
                }
                data-testid={`client-nav-${it.label.toLowerCase()}`}
              >
                {it.icon} {it.label}
              </NavLink>
            ))}
            <GetAppButton className="!w-auto whitespace-nowrap !px-6 !py-4" />
          </div>
        </div>
        <main className="p-4 sm:p-6 lg:p-10 min-w-0">
          <Outlet />
        </main>
      </div>
      <BotChat />
      <WhatsAppBubble />
    </div>
  );
}
